import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { newsAPI } from '../services/api';
import { ArrowLeft, Calendar, User, MapPin, ExternalLink, Pencil, Trash2, Loader2, Heart, MessageCircle, Share2 } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';
import { getCategoryBadgeClass } from '../constants/newsCategoryColors';

const CATEGORY_LABELS: Record<string, string> = {
  equipment_materials: 'Equipment & Materials',
  research_development: 'Research & Development',
  finance_economic: 'Finance & Economic',
  weather: 'Weather',
  others: 'Others',
};

const getVideoEmbedUrl = (url: string): string | null => {
  if (!url?.trim()) return null;
  const u = url.trim();
  const youtubeMatch = u.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  if (youtubeMatch?.[1]) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  const vimeoMatch = u.match(/(?:vimeo\.com\/)(?:video\/)?(\d+)/);
  if (vimeoMatch?.[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const NewsDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));
  const [news, setNews] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [liking, setLiking] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    if (!showShareMenu) return;
    const close = () => setShowShareMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showShareMenu]);

  useEffect(() => {
    if (!id) return;
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await newsAPI.getCommunityNewsView(id);
        if (res.data?.success && res.data?.data) {
          setNews(res.data.data);
        } else {
          setError('News not found');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'News not found');
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [id]);

  const handleLike = async () => {
    if (!user?._id || !news?._id || liking) return;
    setLiking(true);
    try {
      const res = await newsAPI.likeNews(news._id);
      if (res.data?.success && res.data.data) {
        setNews((prev: any) => ({ ...prev, likes: res.data.data.likes || [] }));
      }
    } catch (err) {
      console.error('Like error:', err);
    } finally {
      setLiking(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id || !news?._id || !commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      const res = await newsAPI.addComment(news._id, commentText.trim());
      if (res.data?.success) {
        setNews((prev: any) => ({ ...prev, comments: res.data.data?.comments || [] }));
        setCommentText('');
      }
    } catch (err) {
      console.error('Comment error:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!news?._id) return;
    try {
      const res = await newsAPI.deleteComment(news._id, commentId);
      if (res.data?.success) {
        setNews((prev: any) => ({ ...prev, comments: res.data.data?.comments || [] }));
      }
    } catch (err) {
      console.error('Delete comment error:', err);
    }
  };

  const shareNews = (platform: string) => {
    const shareUrl = `${window.location.origin}/news/${news?._id}`;
    const title = encodeURIComponent(news?.title || '');
    const text = encodeURIComponent((news?.description || '').replace(/<[^>]*>/g, '').substring(0, 200));
    const encodedUrl = encodeURIComponent(shareUrl);
    let link = '';
    if (platform === 'facebook') link = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    else if (platform === 'twitter') link = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${title}`;
    else if (platform === 'linkedin') link = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    else if (platform === 'email') link = `mailto:?subject=${title}&body=${text}%20${encodedUrl}`;
    if (link) {
      window.open(link, '_blank', 'width=600,height=400');
      newsAPI.recordShare(news._id).catch(() => {});
    }
    setShowShareMenu(false);
  };

  const handleDelete = async () => {
    if (!news?._id || !window.confirm('Are you sure you want to delete this news?')) return;
    setDeleting(true);
    try {
      const res = await newsAPI.deleteCommunityNews(news._id);
      if (res.data?.success) {
        navigate('/news');
      } else {
        setError(res.data?.message || 'Failed to delete');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !news) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/news" className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  const imageUrl = news?.image ? getImageUrl(news.image) : null;
  const articleUrl = news?.url && (news.url.startsWith('http') ? news.url : `https://${news.url}`);
  const isCreatorOrAdmin = user && (user._id === (news?.creator?._id || news?.creator) || user.role?.toLowerCase() === 'admin');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link to="/news" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to News
          </Link>
        </div>

        <article className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
          {imageUrl && (
            <div className="w-full aspect-video bg-gray-100">
              <img src={imageUrl} alt="" className="w-full h-full object-cover" />
              {news?.imageCaption && (
                <p className="px-4 py-2 text-sm text-gray-500 bg-gray-50">{news.imageCaption}</p>
              )}
            </div>
          )}

          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {news?.category && (
                <span className={`px-2 py-0.5 rounded text-sm font-medium ${getCategoryBadgeClass(news.category)}`}>
                  {CATEGORY_LABELS[news.category] || news.category}
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{news?.title}</h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              {news?.publishDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(news.publishDate).toLocaleDateString(undefined, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              )}
              {news?.authorName && (
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {news.authorName}
                </span>
              )}
              {news?.creator?.name && !news?.authorName && (
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {news.creator.name}
                </span>
              )}
              {(news?.country || news?.region || news?.city) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {[news.country, news.region, news.city].filter(Boolean).join(', ')}
                </span>
              )}
            </div>

            <div className="prose prose-gray max-w-none mb-6">
              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">{news?.description}</p>
            </div>

            {news?.videoUrl && getVideoEmbedUrl(news.videoUrl) && (
              <div className="mb-6">
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-900">
                  <iframe
                    src={getVideoEmbedUrl(news.videoUrl)!}
                    title={news.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>
            )}

            {articleUrl && (
              <a
                href={articleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
              >
                <ExternalLink className="h-4 w-4" />
                Read full article
              </a>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200 flex flex-wrap items-center gap-4">
              {user?._id ? (
                <button
                  onClick={handleLike}
                  disabled={liking}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    (news?.likes || []).some((l: any) => String(l.user?._id || l.user) === String(user._id))
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {liking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" fill={(news?.likes || []).some((l: any) => String(l.user?._id || l.user) === String(user._id)) ? 'currentColor' : 'none'} />}
                  {(news?.likes || []).length} Like
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 text-sm">
                  <Heart className="h-4 w-4" />
                  {(news?.likes || []).length} Like
                </span>
              )}
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-sm">
                <MessageCircle className="h-4 w-4" />
                {(news?.comments || []).length} Comments
              </span>
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowShareMenu(!showShareMenu); }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-medium"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
                {showShareMenu && (
                  <div className="absolute left-0 top-full mt-1 py-2 bg-white rounded-lg shadow-lg border border-gray-200 z-10 min-w-[140px]">
                    <button onClick={() => shareNews('facebook')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                      Facebook
                    </button>
                    <button onClick={() => shareNews('twitter')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                      Twitter
                    </button>
                    <button onClick={() => shareNews('linkedin')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                      LinkedIn
                    </button>
                    <button onClick={() => shareNews('email')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                      Email
                    </button>
                  </div>
                )}
              </div>
            </div>

            {news?.allowComments !== false && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">{(news?.comments || []).length} Comments</h3>
                {user?._id ? (
                  <form onSubmit={handleAddComment} className="mb-6">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Write a comment..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      maxLength={1000}
                    />
                    <button
                      type="submit"
                      disabled={!commentText.trim() || submittingComment}
                      className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {submittingComment ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null} Post
                    </button>
                  </form>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">
                    <Link to="/login" className="text-indigo-600 hover:underline">Log in</Link> to add a comment
                  </p>
                )}
                <ul className="space-y-4">
                  {(news?.comments || []).map((c: any) => (
                    <li key={c._id} className="flex gap-3">
                      <div className="flex-1 bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 text-sm">{c.user?.name || 'User'}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              {c.createdAt && new Date(c.createdAt).toLocaleDateString()}
                            </span>
                            {(user?._id === (c.user?._id || c.user) || isCreatorOrAdmin) && (
                              <button
                                onClick={() => handleDeleteComment(c._id)}
                                className="text-xs text-red-600 hover:underline"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm mt-1">{c.content}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isCreatorOrAdmin && (
              <div className="mt-8 pt-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => navigate(`/news/edit/${news._id}`)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium text-sm disabled:opacity-50"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete
                </button>
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default NewsDetailPage;
